import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ToolDashboardEntry } from '../../utils/dashboard';
import { ANOMALY_THRESHOLD } from '../../utils/dashboard';
import { finalStateTone } from '../../utils/stateTone';

type Props = {
  entries: ToolDashboardEntry[];
  avgPieces: number;
};

export function AnomaliesPanel({ entries, avgPieces }: Props) {
  const { t } = useTranslation();

  const anomalies = entries
    .filter((e) => e.totalPieces > 0 && e.totalPieces < ANOMALY_THRESHOLD * avgPieces)
    .sort((a, b) => a.totalPieces - b.totalPieces);

  if (!anomalies.length) return null;

  return (
    <div className="max-h-72 overflow-x-auto overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-card">
          <tr className="border-b border-border text-muted-foreground">
            <th className="text-left py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.tool')}</th>
            <th className="text-right py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.pieces')}</th>
            <th className="text-right py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.vsAvg')}</th>
            <th className="text-left py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.machine')}</th>
            <th className="text-left py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.operator')}</th>
            <th className="text-left py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.material')}</th>
            <th className="text-right py-2 pr-3 font-medium">{t('analytics.dashboard.anomalies.col.vc')}</th>
            <th className="text-left py-2 font-medium">{t('analytics.dashboard.anomalies.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {anomalies.map((entry) => {
            const delta = avgPieces > 0 ? Math.round(((entry.totalPieces - avgPieces) / avgPieces) * 100) : 0;
            // Broken tools in the error tone; any other under-performing tool is to investigate.
            const isBroken = finalStateTone(entry.finalState) === 'error';
            return (
              <tr key={entry.toolId} className="border-b border-border/50 hover:bg-muted/40">
                <td className="py-2 pr-3 font-mono">
                  <Link
                    to={`/traceability/tools/${encodeURIComponent(entry.toolId)}`}
                    className="text-primary hover:underline"
                  >
                    {entry.toolId}
                  </Link>
                </td>
                <td className="py-2 pr-3 text-right">{entry.totalPieces}</td>
                <td className={cn('py-2 pr-3 text-right', isBroken ? 'text-destructive' : 'text-warning')}>
                  {delta}%
                </td>
                <td className="py-2 pr-3">{entry.machine ?? '—'}</td>
                <td className="py-2 pr-3">{entry.operator ?? '—'}</td>
                <td className="py-2 pr-3">{entry.material ?? '—'}</td>
                <td className="py-2 pr-3 text-right">{entry.avgVc != null ? Math.round(entry.avgVc) : '—'}</td>
                <td className="py-2">
                  <Badge tone={isBroken ? 'error' : 'warning'}>
                    {isBroken
                      ? t('analytics.dashboard.anomalies.badge.broken')
                      : t('analytics.dashboard.anomalies.badge.investigate')}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
